// src/pages/Dashboard/Admin/HeuresEnseignement/components/EditTeachingHoursDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes, FaTrash } from "react-icons/fa";

import { formatMonthFR } from "../utils/teachingHoursUtils";

export default function EditTeachingHoursDrawer({
  isOpen,
  onClose,
  hour,
  teacherName,
  onSubmit,
  onDelete,
  saving,
  deletingId,
}) {
  const [hours, setHours] = useState("");

  useEffect(() => {
    if (!isOpen || !hour) return;

    setHours(hour?.heures !== undefined && hour?.heures !== null ? String(hour.heures) : "");
  }, [isOpen, hour]);

  const canSubmit = useMemo(() => {
    const value = Number(hours);
    return hour?.id && Number.isFinite(value) && value >= 0 && !saving;
  }, [hour?.id, hours, saving]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const value = Number(hours);

    if (!canSubmit) return;

    await onSubmit?.(hour.id, {
      heures: value,
    });
  };

  if (!isOpen || !hour) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Modification
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Modifier les heures
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Ajustez uniquement le volume horaire de cette entrée.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Contexte
            </p>

            <div className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Enseignant
                </p>
                <p className="font-bold text-slate-900 dark:text-white">
                  {teacherName || "—"}
                </p>
              </div>

              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Semaine
                </p>
                <p className="font-bold text-slate-900 dark:text-white">
                  {hour.semaine || "—"}
                </p>
              </div>

              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Mois
                </p>
                <p className="font-bold text-slate-900 dark:text-white">
                  {formatMonthFR(hour.mois)}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Heures
            </label>

            <input
              type="number"
              step="0.25"
              min="0"
              value={hours}
              onChange={(event) => setHours(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              autoFocus
              required
            />

            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Saisissez un nombre d’heures valide. Les quarts d’heure sont
              acceptés.
            </p>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => onDelete?.(hour)}
              disabled={deletingId === hour.id || saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
            >
              <FaTrash />
              Supprimer
            </button>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}